// StaleBulkSortBanner — a bulk sort runs for one ISO week, Monday to Sunday
// (Thomas, T2). From Monday, a bulk-sort run still open (tipping or stopped)
// from an earlier week is named here with a way to open it and close it. It
// reads its own runs, so the register's filters never hide one. It asks; it
// never closes a run itself.

import { useQuery } from '@tanstack/react-query'
import { CalendarClock } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import { useLinkedRunsOn } from '@/hooks/useLinkedRuns'
import { staleBulkSortRuns } from '@/lib/runFlowGroups'
import { PACKING_OPEN_STATUSES } from '@/lib/runStatus'

interface RunLite { run_id: string; run_number: string; run_kind?: string | null; status?: string | null; started_at: string }

function useOpenBulkSorts(enabled: boolean) {
  const tenantId = useActiveTenantId()
  return useQuery({
    queryKey: ['open-bulk-sorts', tenantId],
    enabled: enabled && !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase.from('runs')
        .select('run_id, run_number, run_kind, status, started_at')
        .eq('tenant_id', tenantId!).eq('run_kind', 'BULK_SORT')
        .in('status', [...PACKING_OPEN_STATUSES]).is('deleted_at', null)
        .order('started_at').limit(50)
      if (error) throw error
      return (data ?? []) as RunLite[]
    },
  })
}

export default function StaleBulkSortBanner({ onOpen, nowMs = Date.now() }: {
  onOpen: (runId: string) => void
  nowMs?: number
}) {
  const linkedOn = useLinkedRunsOn().data === true
  const q = useOpenBulkSorts(linkedOn)
  const stale = staleBulkSortRuns(q.data ?? [], nowMs)
  if (stale.length === 0) return null
  return (
    <div className="card border border-warning-edge bg-warning-soft space-y-2" role="status">
      <p className="text-sm text-warning flex items-center gap-1.5">
        <CalendarClock size={16} /> A bulk sort runs for one week, Monday to Sunday.
      </p>
      {stale.map(r => (
        <div key={r.run_id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="text-fg-secondary">{r.run_number} is from an earlier week. Close it, then start this week&apos;s bulk sort.</span>
          <button type="button" className="btn-secondary btn-sm" onClick={() => onOpen(r.run_id)}>Open {r.run_number}</button>
        </div>
      ))}
    </div>
  )
}
